<script setup lang="ts">
import axios from 'axios'
import { nextTick, onBeforeUnmount, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import {
  $acceptWaitingRoomFile,
  $getS3FileDownloadUrls,
  $getWaitingRoomFileDownloadUrl,
  $getWaitingRoomFilePreviewUrl,
  $getWaitingRoomFiles,
  $getWaitingRoomOptions,
  $getWaitingRoomProjectOptions,
  $rejectWaitingRoomFile,
  $resolveWaitingRoomOnlyDuplicate,
  $restoreWaitingRoomFile,
  $updateWaitingRoomFile,
  WAITING_ROOM_ISSUE_TYPE_OPTIONS,
  WAITING_ROOM_PROJECT_TYPE_OPTIONS,
  getWaitingRoomProjectTypeQueryValue,
  getWaitingRoomProjectTypeTitle,
  type WaitingRoomFile,
  type WaitingRoomIssueType,
  type WaitingRoomIssueTypeQueryValue,
  type WaitingRoomProjectOption,
  type WaitingRoomProjectType,
  type WaitingRoomProjectTypeQueryValue,
  type WaitingRoomStatusMismatch,
} from '@/api/fileManagement'
import { enableDivTableDragScroll } from '@/helpers/global-items/enableDivTableDragScroll'
import { getUserTimezone } from '@/helpers/utils/dateFormatterHelper'
import { isPreviewableFile } from '@/helpers/utils/files/filePreview'
import { getFileIcon, getFileIconColor } from '@/helpers/utils/files/getFileIcon'
import type { TableHeader } from '@/models/general/table'
import { useSnackbarStore } from '@/store/snackbar'
import { onWaitingRoomAutoSyncRefresh } from '@/helpers/events/waitingRoomAutoSync'
import type { DivTableSortItem } from '@/components/vuetify-custom-components/DivTable.vue'

type CampaignOption = {
  id: number
  name: string
  type?: number
  event_group?: number | null
  event_group_name?: string | null
}

type EventGroupOption = {
  id: number
  name: string
}

type WaitingRoomRow = WaitingRoomFile & {
  actionLoading: 'accept' | 'reject' | 'restore' | null
}

type DuplicateResolution = 'waiting_room_file' | 'current_file'
type WaitingRoomChatItemType = 'waiting_room_file' | 'file_management_in_waiting_room_file'
type DuplicateComparisonConfirmPayload = {
  duplicateResolution: DuplicateResolution
  waitingRoomFileId: string
  rejectWaitingRoomFileIds: string[]
  comparisonMode?: 'file_management' | 'waiting_room_only'
  candidateFileId?: string
}

type WaitingRoomTableOptions = {
  page: number
  itemsPerPage: number
  sortBy: DivTableSortItem[]
}

type WaitingRoomIssueTypeFilter = WaitingRoomIssueTypeQueryValue
type WaitingRoomProjectTypeFilter = WaitingRoomProjectTypeQueryValue

const DUPLICATE_FILES_ISSUE_TYPE: WaitingRoomIssueType = 3
const DUPLICATE_FILES_AND_STATUS_MISMATCH_ISSUE_TYPE: WaitingRoomIssueType = 4
const WAITING_ROOM_ONLY_DUPLICATE_ISSUE_TYPE: WaitingRoomIssueType = 5
const STATUS_MISMATCH_ISSUE_TYPE: WaitingRoomIssueType = 2
const PROJECT_CHANGE_OPTIONS_PER_PAGE = 20
const BRAND_BOOK_CODE_ISSUE_FILTERS = new Set<WaitingRoomIssueTypeFilter>([
  'status-mismatch',
  'duplicate-files',
  'all',
])
const BRAND_BOOK_CODE_PROJECT_FILTERS = new Set<WaitingRoomProjectTypeFilter>([
  'branding',
  'all',
])

interface Props {
  active?: boolean
  defaultProjectTypeId?: number | null
  initialProjects?: CampaignOption[]
  syncQuery?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  active: true,
  initialProjects: () => ([]),
  syncQuery: false,
})

interface Emit {
  (e: 'open-page', event: MouseEvent): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()
const route = useRoute()
const router = useRouter()

const rows = ref<WaitingRoomRow[]>([])
const rowsLoading = ref(false)
const isInitializing = ref(false)
const selectedProjects = ref<CampaignOption[]>([])
const selectedPrograms = ref<EventGroupOption[]>([])
const searchFilename = ref<string | null>('')
const selectedIssueTypeFilter = ref<WaitingRoomIssueTypeFilter>('all')
const selectedProjectTypeFilter = ref<WaitingRoomProjectTypeFilter>('all')
const campaignOptions = ref<CampaignOption[]>([])
const eventGroupOptions = ref<EventGroupOption[]>([])
const showRejected = ref(false)
const currentPage = ref(1)
const perPage = ref(15)
const totalRows = ref(0)
const sortBy = ref<DivTableSortItem[]>([])
const showChatDialog = ref(false)
const selectedChatItemId = ref<string | null>(null)
const selectedChatItemType = ref<WaitingRoomChatItemType>('waiting_room_file')
const hasChatMessageChanges = ref(false)
const loadingDownload = ref<Set<string>>(new Set())
const bulkDownloadLoading = ref(false)
const selectedRowIds = ref<string[]>([])
const selectedRowsById = ref<Map<string, WaitingRoomRow>>(new Map())
const loadingPreview = ref<Set<string>>(new Set())
const showPreviewDialog = ref(false)
const isPreviewLoading = ref(false)
const previewUrl = ref<string | null>(null)
const previewFilename = ref('')
const showRenameDialog = ref(false)
const renameLoading = ref(false)
const selectedRenameFile = ref<WaitingRoomRow | null>(null)
const showProjectDialog = ref(false)
const projectLoading = ref(false)
const selectedProjectFile = ref<WaitingRoomRow | null>(null)
const selectedProjectForChange = ref<CampaignOption | null>(null)
const projectChangeOptions = ref<CampaignOption[]>([])
const projectChangeOptionsLoading = ref(false)
const hasMoreProjectChangeOptions = ref(false)
const projectChangeOptionsPage = ref(1)
const projectChangeSearch = ref('')
const showDuplicateComparisonDialog = ref(false)
const pendingDuplicateApproveRow = ref<WaitingRoomRow | null>(null)
const showStatusMismatchDialog = ref(false)
const statusMismatchDialogTitle = ref('Waiting Room - Incorrect status')
const statusMismatchDialogItems = ref<WaitingRoomStatusMismatch[]>([])
let latestProjectChangeOptionsRequest = 0
let cleanupTableDragScroll: (() => void) | null = null
let latestRowsRequest = 0
let latestFilterOptionsRequest = 0
let latestViewChange = 0
let rowsRequestController: AbortController | null = null
let filterOptionsController: AbortController | null = null

const cancelRowsRequest = () => {
  latestRowsRequest += 1
  rowsRequestController?.abort()
  rowsRequestController = null
}

const cancelFilterOptionsRequest = () => {
  latestFilterOptionsRequest += 1
  filterOptionsController?.abort()
  filterOptionsController = null
}

const startViewChange = () => {
  latestViewChange += 1
  cancelRowsRequest()
  rowsLoading.value = true

  return latestViewChange
}

const stopWaitingRoomReads = () => {
  latestViewChange += 1
  cancelRowsRequest()
  cancelFilterOptionsRequest()
  rowsLoading.value = false
}

const isCurrentViewChange = (viewChange: number) => (
  props.active && viewChange === latestViewChange
)

const searchFilenameQuery = computed(() => (searchFilename.value || '').toString().trim())
const selectedIssueType = computed<WaitingRoomIssueType | null>(() => {
  return WAITING_ROOM_ISSUE_TYPE_OPTIONS.find(option => option.queryValue === selectedIssueTypeFilter.value)?.value ?? null
})
const selectedProjectType = computed<WaitingRoomProjectType | null>(() => {
  return WAITING_ROOM_PROJECT_TYPE_OPTIONS.find(option => option.queryValue === selectedProjectTypeFilter.value)?.value ?? null
})
const shouldShowBrandBookCodeColumn = computed(() => (
  BRAND_BOOK_CODE_ISSUE_FILTERS.has(selectedIssueTypeFilter.value)
  && BRAND_BOOK_CODE_PROJECT_FILTERS.has(selectedProjectTypeFilter.value)
))
const shouldShowStatusColumns = computed(() => ['status-mismatch', 'all'].includes(selectedIssueTypeFilter.value))
const selectedProjectIds = computed(() => selectedProjects.value.map(project => project.id))
const selectedProgramIds = computed(() => selectedPrograms.value.map(program => program.id))
const perPageOptions = [15, 20, 50, 100]
const issueTypeFilterOptions: Array<{ title: string, value: WaitingRoomIssueTypeFilter }> = [
  ...WAITING_ROOM_ISSUE_TYPE_OPTIONS.map(option => ({
    title: option.title,
    value: option.queryValue,
  })),
]
const projectTypeFilterOptions: Array<{ title: string, value: WaitingRoomProjectTypeFilter }> = [
  ...WAITING_ROOM_PROJECT_TYPE_OPTIONS.map(option => ({
    title: option.title,
    value: option.queryValue,
  })),
]
const totalPages = computed(() => Math.max(Math.ceil(totalRows.value / perPage.value), 1))
const visibleRangeText = computed(() => {
  if (!totalRows.value) return '0-0 of 0'

  const start = ((currentPage.value - 1) * perPage.value) + 1
  const end = Math.min(currentPage.value * perPage.value, totalRows.value)

  return `${start}-${end} of ${totalRows.value}`
})
const waitingRoomTableBaseId = computed(() => {
  const context = props.syncQuery ? 'page' : 'dialog'
  const state = showRejected.value ? 'rejected' : 'pending'

  return `project-files-waiting-room-${context}-${state}-table`
})
const waitingRoomTableLayoutSuffix = computed(() => {
  if (shouldShowStatusColumns.value) {
    return shouldShowBrandBookCodeColumn.value ? 'status-brand-book' : 'status'
  }

  return shouldShowBrandBookCodeColumn.value ? 'brand-book' : 'standard'
})
const waitingRoomTableRenderId = computed(() => (
  `${waitingRoomTableBaseId.value}-${waitingRoomTableLayoutSuffix.value}`
))
const waitingRoomTableStorageId = computed(() => `${waitingRoomTableRenderId.value}-v3`)

const syncTableDragScroll = async () => {
  cleanupTableDragScroll?.()
  cleanupTableDragScroll = null

  await nextTick()
  cleanupTableDragScroll = enableDivTableDragScroll('waiting-room-table')
}

watch(waitingRoomTableRenderId, () => {
  void syncTableDragScroll()
}, { immediate: true })

onBeforeUnmount(() => {
  stopWaitingRoomReads()
  unsubscribeWaitingRoomAutoSyncRefresh()
  cleanupTableDragScroll?.()
  cleanupTableDragScroll = null
})
const waitingRoomHeaders = computed<TableHeader[]>(() => {
  const headers: TableHeader[] = [
    { key: 'new_filename', title: 'File Name', align: '', sortable: true, width: 190 },
    { key: 'filename', title: 'Uploaded File Name', align: '', sortable: true, width: 150 },
    { key: 'folder', title: 'Folder', align: '', sortable: true, width: 130 },
    { key: 'issue_type', title: 'Issue Type', align: '', sortable: true, width: 130 },
    { key: 'campaign_type', title: 'Project Type', align: '', sortable: true, width: 90 },
    { key: 'campaign_name', title: 'Project', align: '', sortable: true, width: 160 },
    { key: 'created_by', title: 'User', align: '', sortable: true, width: 120 },
    { key: 'created', title: 'Upload Date', align: '', sortable: true, width: 97 },
  ]

  if (shouldShowBrandBookCodeColumn.value) {
    headers.push({ key: 'brand_book_codes', title: 'Brand Book Code', align: '', sortable: false, width: 160 })
  }

  if (shouldShowStatusColumns.value) {
    headers.push(
      { key: 'current_statuses', title: 'Current Status', align: '', sortable: false, width: 160 },
      { key: 'expected_statuses', title: 'Expected Status', align: '', sortable: false, width: 160 },
    )
  }

  headers.push({ key: 'actions', title: 'Actions', align: '', sortable: false, width: showRejected.value ? 250 : 350 })

  return headers
})

const previewDialogVisible = computed({
  get: () => showPreviewDialog.value,
  set: value => {
    showPreviewDialog.value = value
    if (!value) {
      previewUrl.value = null
      previewFilename.value = ''
      isPreviewLoading.value = false
    }
  },
})

const baseRequestParams = computed(() => ({
  is_rejected: showRejected.value,
  search: searchFilenameQuery.value || undefined,
  issue_type: selectedIssueType.value || undefined,
  campaign_type: selectedProjectType.value ?? undefined,
}))

const requestParams = computed(() => ({
  ...baseRequestParams.value,
  campaign_ids: selectedProjectIds.value.length ? selectedProjectIds.value.join(',') : undefined,
  program_ids: selectedProgramIds.value.length ? selectedProgramIds.value.join(',') : undefined,
  page: currentPage.value,
  per_page: perPage.value,
  ordering: sortBy.value[0]
    ? `${sortBy.value[0].order === 'desc' ? '-' : ''}${sortBy.value[0].key}`
    : undefined,
}))

const normalizeErrorValue = (input: unknown): string => {
  if (typeof input === 'string') return input
  if (Array.isArray(input)) return input.map(item => normalizeErrorValue(item)).filter(Boolean).join(', ')
  if (input && typeof input === 'object') {
    return Object.values(input as Record<string, unknown>).map(item => normalizeErrorValue(item)).filter(Boolean).join(', ')
  }
  return ''
}

const getBackendErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail
  const detailMessage = normalizeErrorValue(detail)
  if (detailMessage) return detailMessage
  const message = error?.response?.data?.message
  const messageText = normalizeErrorValue(message)
  if (messageText) return messageText
  return error?.message || 'Request failed'
}

const getStatusMismatches = (error: any): WaitingRoomStatusMismatch[] => {
  const responseData = error?.response?.data
  if (
    responseData?.error_code !== 'incorrect_status'
    || !Array.isArray(responseData?.status_mismatches)
  ) {
    return []
  }

  return responseData.status_mismatches
}

const formatStatusMismatch = (item: WaitingRoomStatusMismatch): string => {
  return `${item.item_label} ${item.identity} (#${item.item_id}): `
    + `Current status: ${item.current_status}. `
    + `Expected system status: ${item.expected_system_status}`
}

const isStatusMismatchRow = (row: WaitingRoomRow): boolean => row.issue_type === STATUS_MISMATCH_ISSUE_TYPE
const isVirtualRow = (row: WaitingRoomRow): boolean => Boolean(row.is_virtual)
const isDuplicateFilesRow = (row: WaitingRoomRow): boolean => (
  row.issue_type === DUPLICATE_FILES_ISSUE_TYPE
  || row.issue_type === DUPLICATE_FILES_AND_STATUS_MISMATCH_ISSUE_TYPE
  || row.issue_type === WAITING_ROOM_ONLY_DUPLICATE_ISSUE_TYPE
)

const getBrandBookCodes = (row: WaitingRoomRow): string[] => {
  if (!Array.isArray(row.brand_book_codes)) return []

  return row.brand_book_codes
    .map(code => (code || '').toString().trim())
    .filter(Boolean)
}

const getBrandBookCodeStates = (row: WaitingRoomRow): Array<{ code: string, passed: boolean }> => {
  if (!Array.isArray(row.brand_book_code_states)) return []

  return row.brand_book_code_states.filter(state => Boolean(state?.code))
}

const getVirtualFileUrl = async (
  row: WaitingRoomRow,
  intent: 'preview' | 'download',
): Promise<string> => {
  if (!row.source_file_id || row.source_file_campaign_id == null) {
    throw new Error('File Management source is unavailable')
  }

  const metadata = await $getS3FileDownloadUrls(
    row.source_file_campaign_id,
    {
      file_id: row.source_file_id,
      intent,
    },
    900,
  )
  const url = intent === 'preview'
    ? metadata?.url ?? metadata?.download_url
    : metadata?.download_url
  if (!url) throw new Error(`No ${intent} URL returned`)

  return url
}

const getRowFileUrl = async (
  row: WaitingRoomRow,
  intent: 'preview' | 'download',
): Promise<string> => {
  if (isVirtualRow(row)) return await getVirtualFileUrl(row, intent)

  const response = intent === 'preview'
    ? await $getWaitingRoomFilePreviewUrl(row.id)
    : await $getWaitingRoomFileDownloadUrl(row.id)
  const url = response.data?.url ?? response.data?.download_url
  if (!url) throw new Error(`No ${intent} URL returned`)

  return url
}

const formatStatuses = (statuses: string[]): string => statuses.join(', ')

const isNotFoundError = (error: any): boolean => error?.response?.status === 404

const getQueryString = (value: unknown): string => {
  if (Array.isArray(value)) return value[0]?.toString() || ''
  return value?.toString() || ''
}

const parsePositiveInt = (value: unknown, fallback: number): number => {
  const parsed = Number.parseInt(getQueryString(value), 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const parsePositiveIds = (value: unknown): number[] => {
  const rawValues = Array.isArray(value) ? value : [value]
  const ids: number[] = []
  rawValues.forEach(rawValue => {
    getQueryString(rawValue)
      .split(',')
      .map(item => Number.parseInt(item.trim(), 10))
      .filter(item => Number.isFinite(item) && item > 0)
      .forEach(item => ids.push(item))
  })

  return [...new Set(ids)]
}

const sameIdList = (left: number[], right: number[]) => {
  if (left.length !== right.length) return false
  return left.every((item, index) => item === right[index])
}

const getDefaultProjectTypeFilter = (): WaitingRoomProjectTypeFilter => {
  if (props.defaultProjectTypeId === undefined || props.defaultProjectTypeId === null) {
    return 'all'
  }

  return getWaitingRoomProjectTypeQueryValue(props.defaultProjectTypeId)
}

const applyQueryState = () => {
  searchFilename.value = getQueryString(route.query.search)
  const issueTypeQuery = getQueryString(route.query['issue-type'])
  selectedIssueTypeFilter.value = issueTypeFilterOptions.some(option => option.value === issueTypeQuery)
    ? issueTypeQuery as WaitingRoomIssueTypeFilter
    : 'all'
  const projectTypeQuery = getQueryString(route.query['project-type'])
  selectedProjectTypeFilter.value = projectTypeFilterOptions.some(option => option.value === projectTypeQuery)
    ? projectTypeQuery as WaitingRoomProjectTypeFilter
    : getDefaultProjectTypeFilter()
  showRejected.value = getQueryString(route.query['waiting-room-type']) === 'rejected'
  currentPage.value = parsePositiveInt(route.query.page, 1)

  const nextPerPage = parsePositiveInt(route.query['per-page'] || route.query.per_page, 15)
  perPage.value = perPageOptions.includes(nextPerPage) ? nextPerPage : 15

  const projectIds = new Set(parsePositiveIds(route.query.projects))
  const programIds = new Set(parsePositiveIds(route.query.programs))
  selectedProjects.value = campaignOptions.value.filter(project => projectIds.has(project.id))
  selectedPrograms.value = eventGroupOptions.value.filter(program => programIds.has(program.id))
}

const syncQueryFromState = async () => {
  if (!props.syncQuery) return

  const query = { ...route.query } as Record<string, any>
  if (searchFilenameQuery.value) {
    query.search = searchFilenameQuery.value
  } else {
    delete query.search
  }

  query['issue-type'] = selectedIssueTypeFilter.value
  query['project-type'] = selectedProjectTypeFilter.value

  query['waiting-room-type'] = showRejected.value ? 'rejected' : 'default'
  query.page = String(currentPage.value)
  query['per-page'] = String(perPage.value)
  delete query.per_page

  if (selectedProjectIds.value.length) {
    query.projects = selectedProjectIds.value.join(',')
  } else {
    delete query.projects
  }

  if (selectedProgramIds.value.length) {
    query.programs = selectedProgramIds.value.join(',')
  } else {
    delete query.programs
  }

  await router.replace({ query })
}

const makeWaitingRoomPageQuery = () => {
  const query: Record<string, string> = {
    'waiting-room-type': showRejected.value ? 'rejected' : 'default',
    page: String(currentPage.value),
    'per-page': String(perPage.value),
  }

  if (searchFilenameQuery.value) {
    query.search = searchFilenameQuery.value
  }

  query['issue-type'] = selectedIssueTypeFilter.value
  query['project-type'] = selectedProjectTypeFilter.value
  if (selectedProjectIds.value.length) {
    query.projects = selectedProjectIds.value.join(',')
  }
  if (selectedProgramIds.value.length) {
    query.programs = selectedProgramIds.value.join(',')
  }

  return query
}

const waitingRoomPageLocation = computed(() => ({
  path: '/file-management/waiting-room',
  query: makeWaitingRoomPageQuery(),
}))

const onWaitingRoomPageLinkClick = (event: MouseEvent) => {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
  ) {
    return
  }

  window.setTimeout(() => emit('open-page', event), 0)
}

const formatFixedDateTime = (value?: string | null): string => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: getUserTimezone(),
  }).formatToParts(date)

  const part = (type: string) => parts.find(item => item.type === type)?.value || ''

  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`
}

const toRow = (file: WaitingRoomFile): WaitingRoomRow => ({
  ...file,
  actionLoading: null,
})

const loadRows = async () => {
  if (!props.active) return

  cancelRowsRequest()
  const requestId = latestRowsRequest
  const controller = new AbortController()
  rowsRequestController = controller
  const isCurrentRequest = () => (
    props.active
    && requestId === latestRowsRequest
    && rowsRequestController === controller
    && !controller.signal.aborted
  )
  const retryFirstPage = async () => {
    if (!isCurrentRequest()) return
    currentPage.value = 1
    await syncQueryFromState()
    if (!isCurrentRequest()) return

    const retryRes = await $getWaitingRoomFiles(requestParams.value, controller.signal)
    if (!isCurrentRequest()) return
    rows.value = (retryRes.data.results || []).map(toRow)
    totalRows.value = retryRes.data.count || 0
  }

  rowsLoading.value = true
  try {
    const res = await $getWaitingRoomFiles(requestParams.value, controller.signal)
    if (!isCurrentRequest()) return
    const nextRows = (res.data.results || []).map(toRow)
    const nextTotal = res.data.count || 0

    if (!nextRows.length && nextTotal > 0 && currentPage.value > 1) {
      await retryFirstPage()
      return
    }

    rows.value = nextRows
    totalRows.value = nextTotal
  } catch (error) {
    if (!isCurrentRequest() || axios.isCancel(error)) return

    if (isNotFoundError(error) && currentPage.value > 1) {
      try {
        await retryFirstPage()
        return
      } catch (retryError) {
        error = retryError
      }
    }

    if (!isCurrentRequest() || axios.isCancel(error)) return
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: getBackendErrorMessage(error),
    })
  } finally {
    if (requestId === latestRowsRequest && rowsRequestController === controller) {
      rowsRequestController = null
      rowsLoading.value = false
    }
  }
}

const loadFilterOptions = async () => {
  if (!props.active) return false

  cancelFilterOptionsRequest()
  const requestId = latestFilterOptionsRequest
  const controller = new AbortController()
  filterOptionsController = controller
  const isCurrentRequest = () => (
    props.active
    && requestId === latestFilterOptionsRequest
    && filterOptionsController === controller
    && !controller.signal.aborted
  )

  try {
    const res = await $getWaitingRoomOptions(baseRequestParams.value, controller.signal)
    if (!isCurrentRequest()) return false
    campaignOptions.value = res.data.campaign_options || []
    eventGroupOptions.value = res.data.event_group_options || []

    const availableProjectIds = new Set(campaignOptions.value.map(project => project.id))
    const availableProgramIds = new Set(eventGroupOptions.value.map(program => program.id))
    const nextSelectedProjects = selectedProjects.value.filter(project => availableProjectIds.has(project.id))
    const nextSelectedPrograms = selectedPrograms.value.filter(program => availableProgramIds.has(program.id))
    if (!sameIdList(nextSelectedProjects.map(project => project.id), selectedProjectIds.value)) {
      selectedProjects.value = nextSelectedProjects
    }
    if (!sameIdList(nextSelectedPrograms.map(program => program.id), selectedProgramIds.value)) {
      selectedPrograms.value = nextSelectedPrograms
    }
    return true
  } catch (error) {
    if (!isCurrentRequest() || axios.isCancel(error)) return false
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: getBackendErrorMessage(error),
    })
    return true
  } finally {
    if (requestId === latestFilterOptionsRequest && filterOptionsController === controller) {
      filterOptionsController = null
    }
  }
}

const unsubscribeWaitingRoomAutoSyncRefresh = onWaitingRoomAutoSyncRefresh(result => {
  if (props.active && result.accepted_count > 0) {
    void loadRows()
  }
})

const refresh = async () => {
  await loadRows()
}

const onWaitingRoomTableOptionsUpdate = async (options: WaitingRoomTableOptions) => {
  const nextPage = Number(options.page) || 1
  const nextPerPage = Number(options.itemsPerPage) || perPage.value
  const nextSortBy = options.sortBy || []
  const didChange = nextPage !== currentPage.value
    || nextPerPage !== perPage.value
    || JSON.stringify(nextSortBy) !== JSON.stringify(sortBy.value)

  currentPage.value = nextPage
  perPage.value = nextPerPage
  sortBy.value = nextSortBy

  if (!props.active || !didChange || isInitializing.value) return

  const viewChange = startViewChange()
  await syncQueryFromState()
  if (!isCurrentViewChange(viewChange)) return
  await loadRows()
}

const changePage = async (page: number) => {
  await onWaitingRoomTableOptionsUpdate({
    page,
    itemsPerPage: perPage.value,
    sortBy: [],
  })
}

const changeItemsPerPage = async (itemsPerPage: number) => {
  await onWaitingRoomTableOptionsUpdate({
    page: 1,
    itemsPerPage,
    sortBy: [],
  })
}

const hasApprovalFilename = (row: WaitingRoomRow) => Boolean((row.new_filename || row.name || '').trim())

const showMissingApprovalFilenameWarning = () => {
  snackbarStore.showMessage({
    color: 'warning',
    icon: 'mdi-alert-circle',
    title: 'Waiting Room',
    text: 'New File Name is required before approval.',
  })
}

const approveRow = async (
  row: WaitingRoomRow,
  duplicateResolution?: DuplicateResolution,
  rejectWaitingRoomFileIds: string[] = [],
  waitingRoomFileId = row.id,
  bypassDuplicateStatusValidation = false,
) => {
  row.actionLoading = 'accept'
  try {
    await $acceptWaitingRoomFile(
      waitingRoomFileId,
      duplicateResolution === undefined
        ? undefined
        : {
            duplicate_resolution: duplicateResolution,
            reject_waiting_room_file_ids: rejectWaitingRoomFileIds,
            bypass_duplicate_status_validation: bypassDuplicateStatusValidation,
          },
    )
    await refresh()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Approved',
      text: duplicateResolution === 'current_file'
        ? `${row.filename} rejected. Current file kept.`
        : rejectWaitingRoomFileIds.length
          ? `${row.filename} uploaded to Project. Other matching Waiting Room files rejected.`
          : `${row.filename} uploaded to Project.`,
    })
  } catch (error) {
    await refresh()
    const statusMismatches = getStatusMismatches(error)
    if (statusMismatches.length) {
      if (statusMismatches.length === 1) {
        snackbarStore.showMessage({
          color: 'error',
          icon: 'mdi-alert-circle',
          title: 'Waiting Room - Incorrect status - 1 item(s)',
          lines: [formatStatusMismatch(statusMismatches[0])],
        })
        return
      }

      statusMismatchDialogTitle.value = `Waiting Room - Incorrect status - ${statusMismatches.length} item(s)`
      statusMismatchDialogItems.value = statusMismatches
      showStatusMismatchDialog.value = true
      return
    }

    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: getBackendErrorMessage(error),
    })
  } finally {
    row.actionLoading = null
  }
}

const acceptRow = async (row: WaitingRoomRow) => {
  if (!hasApprovalFilename(row)) {
    showMissingApprovalFilenameWarning()
    return
  }

  if (isDuplicateFilesRow(row)) {
    pendingDuplicateApproveRow.value = row
    showDuplicateComparisonDialog.value = true
    return
  }

  await approveRow(row)
}

const clearDuplicateComparison = () => {
  pendingDuplicateApproveRow.value = null
}

const confirmDuplicateComparison = async (payload: DuplicateComparisonConfirmPayload) => {
  const row = pendingDuplicateApproveRow.value
  pendingDuplicateApproveRow.value = null
  showDuplicateComparisonDialog.value = false
  if (!row) return

  if (payload.comparisonMode === 'waiting_room_only') {
    if (!payload.candidateFileId) return
    row.actionLoading = 'accept'
    try {
      await $resolveWaitingRoomOnlyDuplicate(row.id, {
        candidate_file_id: payload.candidateFileId,
        keep: payload.duplicateResolution === 'current_file'
          ? 'status_mismatch'
          : 'waiting_room_duplicate',
      })
      await refresh()
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Waiting Room Duplicate Resolved',
        text: payload.duplicateResolution === 'current_file'
          ? 'Status Mismatch file kept; Waiting Room duplicate rejected.'
          : 'Selected file is now the Status Mismatch candidate; previous candidate rejected.',
      })
    } catch (error) {
      await refresh()
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Waiting Room Duplicate',
        text: getBackendErrorMessage(error),
      })
    } finally {
      row.actionLoading = null
    }
    return
  }

  await approveRow(
    row,
    payload.duplicateResolution,
    payload.rejectWaitingRoomFileIds,
    payload.waitingRoomFileId,
    payload.duplicateResolution === 'waiting_room_file',
  )
}

const rejectRow = async (row: WaitingRoomRow) => {
  row.actionLoading = 'reject'
  try {
    await $rejectWaitingRoomFile(row.id)
    await refresh()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Rejected',
      text: `${row.filename} moved to Rejected.`,
    })
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: getBackendErrorMessage(error),
    })
  } finally {
    row.actionLoading = null
  }
}

const restoreRow = async (row: WaitingRoomRow) => {
  row.actionLoading = 'restore'
  try {
    const response = await $restoreWaitingRoomFile(row.id)
    await refresh()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Restored',
      text: response.data.is_accepted
        ? `${row.filename} restored to File Management.`
        : `${row.filename} returned to Waiting Room.`,
    })
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: getBackendErrorMessage(error),
    })
  } finally {
    row.actionLoading = null
  }
}

const openChat = (row: WaitingRoomRow) => {
  if (isVirtualRow(row)) {
    if (!row.source_file_id) return

    selectedChatItemId.value = row.source_file_id
    selectedChatItemType.value = 'file_management_in_waiting_room_file'
  } else {
    selectedChatItemId.value = row.id
    selectedChatItemType.value = 'waiting_room_file'
  }
  hasChatMessageChanges.value = false
  showChatDialog.value = true
}

const handleChatMessageAdded = () => {
  hasChatMessageChanges.value = true
}

const handleChatClose = () => {
  const shouldRefreshRows = hasChatMessageChanges.value
  hasChatMessageChanges.value = false
  selectedChatItemId.value = null
  selectedChatItemType.value = 'waiting_room_file'

  if (shouldRefreshRows) {
    void loadRows()
  }
}

const getChatMessagesCount = (row: WaitingRoomRow): number => Number(row.chat_messages_count || 0)

const getChatMessagesCountTooltip = (row: WaitingRoomRow): string => {
  const count = getChatMessagesCount(row)
  const noun = count === 1 ? 'message' : 'messages'

  return `${count} ${noun} in this chat`
}

const previewRow = async (row: WaitingRoomRow) => {
  if (!isPreviewableFile(row.filename)) return

  loadingPreview.value = new Set([...loadingPreview.value, row.id])
  previewFilename.value = row.filename
  previewUrl.value = null
  showPreviewDialog.value = true
  isPreviewLoading.value = true
  try {
    previewUrl.value = await getRowFileUrl(row, 'preview')
  } catch (error) {
    showPreviewDialog.value = false
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Preview Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    const next = new Set(loadingPreview.value)
    next.delete(row.id)
    loadingPreview.value = next
    isPreviewLoading.value = false
  }
}

const downloadRow = async (row: WaitingRoomRow) => {
  loadingDownload.value = new Set([...loadingDownload.value, row.id])

  try {
    const url = await getRowFileUrl(row, 'download')

    const link = document.createElement('a')
    link.href = url
    link.download = row.filename
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    const next = new Set(loadingDownload.value)
    next.delete(row.id)
    loadingDownload.value = next
  }
}

const selectedRows = computed(() => (
  selectedRowIds.value
    .map(id => selectedRowsById.value.get(id))
    .filter((row): row is WaitingRoomRow => Boolean(row))
))

const selectedFileCount = computed(() => selectedRows.value.length)

const updateSelectedRows = (selectedIds: string[]) => {
  const selectedIdSet = new Set(selectedIds)
  const nextSelectedRows = new Map(selectedRowsById.value)

  rows.value.forEach(row => {
    if (selectedIdSet.has(row.id)) {
      nextSelectedRows.set(row.id, row)
    }
  })
  nextSelectedRows.forEach((_row, id) => {
    if (!selectedIdSet.has(id)) {
      nextSelectedRows.delete(id)
    }
  })

  selectedRowIds.value = selectedIds
  selectedRowsById.value = nextSelectedRows
}

const clearSelectedRows = () => {
  selectedRowIds.value = []
  selectedRowsById.value = new Map()
}

const waitForNextDownload = () => new Promise<void>(resolve => {
  window.setTimeout(resolve, 300)
})

const downloadSelectedRows = async () => {
  const filesToDownload = selectedRows.value
  if (!filesToDownload.length || bulkDownloadLoading.value) return

  bulkDownloadLoading.value = true
  let successCount = 0

  for (const row of filesToDownload) {
    try {
      const url = await getRowFileUrl(row, 'download')

      const link = document.createElement('a')
      link.href = url
      link.download = row.filename
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      successCount += 1
    } catch {
      // Report all failed downloads together after the batch completes.
    }

    await waitForNextDownload()
  }

  const failedCount = filesToDownload.length - successCount
  snackbarStore.showMessage({
    color: failedCount ? 'warning' : 'success',
    icon: failedCount ? 'mdi-alert-circle' : 'mdi-checkbox-marked-circle',
    title: failedCount ? 'Download Partially Complete' : 'Download Complete',
    text: failedCount
      ? `${successCount} of ${filesToDownload.length} selected files downloaded. ${failedCount} failed.`
      : `${successCount} selected ${successCount === 1 ? 'file' : 'files'} downloaded.`,
  })
  bulkDownloadLoading.value = false
}

const openRenameDialog = (row: WaitingRoomRow) => {
  selectedRenameFile.value = row
  showRenameDialog.value = true
}

const saveNewFilename = async (newFilename: string) => {
  if (!selectedRenameFile.value) return

  renameLoading.value = true
  try {
    const res = await $updateWaitingRoomFile(selectedRenameFile.value.id, {
      new_filename: newFilename,
    })
    Object.assign(selectedRenameFile.value, toRow(res.data))
    showRenameDialog.value = false
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: res.data.is_accepted ? 'File Uploaded' : 'New File Name Updated',
      text: res.data.is_accepted
        ? `${res.data.new_filename}${res.data.extension || ''} moved to File Management.`
        : `${res.data.new_filename}${res.data.extension || ''}`,
    })
    await loadRows()
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Rename Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    renameLoading.value = false
  }
}

const projectOptionFromRow = (row: WaitingRoomRow): CampaignOption | null => {
  if (!row.campaign_id) return null
  return {
    id: row.campaign_id,
    name: row.campaign_name || `Project #${row.campaign_id}`,
    type: row.campaign_type,
    event_group: row.event_group ?? null,
    event_group_name: row.event_group_name ?? null,
  }
}

const normalizeProjectOption = (project: WaitingRoomProjectOption): CampaignOption => ({
  id: project.id,
  name: project.name,
  type: project.type,
  event_group: project.event_group ?? null,
  event_group_name: project.event_group_name ?? null,
})

const mergeProjectChangeOptions = (options: CampaignOption[], page: number) => {
  const existingOptions = page === 1 ? [] : projectChangeOptions.value
  const optionsById = new Map<number, CampaignOption>()

  existingOptions.forEach(option => optionsById.set(option.id, option))
  options.forEach(option => optionsById.set(option.id, option))

  projectChangeOptions.value = [...optionsById.values()]
}

const loadProjectChangeOptions = async (search = projectChangeSearch.value, page = 1) => {
  const row = selectedProjectFile.value
  if (!row) return

  const requestId = ++latestProjectChangeOptionsRequest
  projectChangeOptionsLoading.value = true
  try {
    const res = await $getWaitingRoomProjectOptions({
      campaign_type: row.campaign_type,
      search: search.trim() || undefined,
      page,
      per_page: PROJECT_CHANGE_OPTIONS_PER_PAGE,
    })
    if (requestId !== latestProjectChangeOptionsRequest) return

    mergeProjectChangeOptions((res.data.results || []).map(normalizeProjectOption), page)
    projectChangeOptionsPage.value = page
    hasMoreProjectChangeOptions.value = Boolean(res.data.next)
  } catch (error) {
    if (requestId !== latestProjectChangeOptionsRequest) return
    projectChangeOptions.value = []
    hasMoreProjectChangeOptions.value = false
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Project Search Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    if (requestId === latestProjectChangeOptionsRequest) {
      projectChangeOptionsLoading.value = false
    }
  }
}

const searchProjectChangeOptions = useDebounceFn((search: string) => {
  void loadProjectChangeOptions(search, 1)
}, 300)

const onProjectChangeSearch = (search: string) => {
  projectChangeSearch.value = search
  projectChangeOptionsPage.value = 1
  hasMoreProjectChangeOptions.value = false
  searchProjectChangeOptions(search)
}

const loadMoreProjectChangeOptions = () => {
  if (projectChangeOptionsLoading.value || !hasMoreProjectChangeOptions.value) return
  void loadProjectChangeOptions(projectChangeSearch.value, projectChangeOptionsPage.value + 1)
}

const openProjectDialog = async (row: WaitingRoomRow) => {
  selectedProjectFile.value = row
  projectChangeSearch.value = ''
  projectChangeOptions.value = []
  projectChangeOptionsPage.value = 1
  hasMoreProjectChangeOptions.value = false
  selectedProjectForChange.value = projectOptionFromRow(row)
  showProjectDialog.value = true
  await loadProjectChangeOptions('', 1)
}

const saveProject = async () => {
  const row = selectedProjectFile.value
  const project = selectedProjectForChange.value
  if (!row || !project) return

  projectLoading.value = true
  try {
    const res = await $updateWaitingRoomFile(row.id, {
      campaign_id: project.id,
    })
    Object.assign(row, toRow(res.data))
    showProjectDialog.value = false
    selectedProjectFile.value = null
    selectedProjectForChange.value = null
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: res.data.is_accepted ? 'File Uploaded' : 'Project Updated',
      text: res.data.is_accepted
        ? `${row.filename} moved to File Management.`
        : res.data.campaign_name || 'Waiting Room file project updated.',
    })
    await loadFilterOptions()
    await refresh()
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Project Update Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    projectLoading.value = false
  }
}

const resetTransientDialogs = () => {
  clearSelectedRows()
  previewUrl.value = null
  previewFilename.value = ''
  showPreviewDialog.value = false
  isPreviewLoading.value = false
  showChatDialog.value = false
  selectedChatItemId.value = null
  selectedChatItemType.value = 'waiting_room_file'
  showRenameDialog.value = false
  selectedRenameFile.value = null
  renameLoading.value = false
  showProjectDialog.value = false
  selectedProjectFile.value = null
  selectedProjectForChange.value = null
  projectChangeOptions.value = []
  projectChangeOptionsPage.value = 1
  projectChangeSearch.value = ''
  hasMoreProjectChangeOptions.value = false
  projectChangeOptionsLoading.value = false
  projectLoading.value = false
  showDuplicateComparisonDialog.value = false
  pendingDuplicateApproveRow.value = null
  showStatusMismatchDialog.value = false
  statusMismatchDialogItems.value = []
  statusMismatchDialogTitle.value = 'Waiting Room - Incorrect status'
}

const initializeBlock = async () => {
  const viewChange = startViewChange()
  isInitializing.value = true

  if (props.syncQuery) {
    applyQueryState()
  } else {
    selectedProjects.value = []
    selectedPrograms.value = []
    searchFilename.value = ''
    selectedIssueTypeFilter.value = 'all'
    selectedProjectTypeFilter.value = getDefaultProjectTypeFilter()
    currentPage.value = 1
  }

  try {
    const optionsReady = await loadFilterOptions()
    if (!optionsReady || !isCurrentViewChange(viewChange)) return
    if (props.syncQuery) {
      applyQueryState()
    }
    if (!isCurrentViewChange(viewChange)) return
    await refresh()
  } finally {
    if (viewChange === latestViewChange) {
      isInitializing.value = false
    }
  }
}

const onFiltersChange = async () => {
  if (!props.active || isInitializing.value) return
  const viewChange = startViewChange()
  clearSelectedRows()
  currentPage.value = 1
  const optionsReady = await loadFilterOptions()
  if (!optionsReady || !isCurrentViewChange(viewChange)) return
  await syncQueryFromState()
  if (!isCurrentViewChange(viewChange)) return
  await refresh()
}

const onSearchFilenameChange = useDebounceFn((viewChange: number) => {
  if (isCurrentViewChange(viewChange)) {
    void onFiltersChange()
  }
}, 350)

onMounted(async () => {
  if (props.active) {
    await initializeBlock()
  }
})

watch(() => props.active, async value => {
  if (value) {
    await initializeBlock()
    return
  }

  stopWaitingRoomReads()
  isInitializing.value = false
  resetTransientDialogs()
})

watch([showRejected, selectedIssueTypeFilter, selectedProjectTypeFilter, selectedProjects, selectedPrograms], onFiltersChange)

watch(showDuplicateComparisonDialog, value => {
  if (!value) {
    pendingDuplicateApproveRow.value = null
  }
})

watch(searchFilename, () => {
  if (!props.active || isInitializing.value) return
  const viewChange = startViewChange()
  cancelFilterOptionsRequest()
  void onSearchFilenameChange(viewChange)
})
</script>

<template>
  <div class="project-files-waiting-room-block">
    <VRow class="waiting-room-filter-row pt-1">
      <VCol cols="12" md="6" lg="3" class="waiting-room-filter-column d-flex align-center">
        <VTextField
          v-model="searchFilename"
          class="w-100"
          label="Search by Filename"
          prepend-inner-icon="tabler-search"
          density="compact"
          clearable
          hide-details
        />
      </VCol>
      <VCol cols="12" md="6" lg="" class="waiting-room-filter-column d-flex align-center">
        <MultipleEventGroupSelect
          v-model="selectedPrograms"
          class="w-100"
          label="Program"
          :select-options="eventGroupOptions"
          :is-select-all-visible="true"
        />
      </VCol>
      <VCol cols="12" md="6" lg="" class="waiting-room-filter-column d-flex align-center">
        <MultipleCampaignsSelect
          v-model="selectedProjects"
          class="w-100"
          label="Project"
          :select-options="campaignOptions"
          :is-select-all-visible="true"
        />
      </VCol>
      <VCol cols="12" md="6" lg="" class="waiting-room-filter-column d-flex align-center justify-md-end">
        <VBtnToggle
          v-model="showRejected"
          class="waiting-room-view-toggle"
          color="primary"
          divided
          mandatory
          variant="outlined"
        >
          <VBtn
            :value="false"
            class="px-4 py-2"
          >
            <span class="text-wrap">Waiting Room</span>
          </VBtn>
          <VBtn
            :value="true"
            class="px-4 py-2"
          >
            <span class="text-wrap">Rejected</span>
          </VBtn>
        </VBtnToggle>
      </VCol>
    </VRow>

    <VRow>
      <VCol
        cols="12"
        class="d-flex align-center flex-wrap pt-0"
      >
        <div class="waiting-room-issue-type-toggle-block mr-3 mt-3">
          <VBtnToggle
            v-model="selectedIssueTypeFilter"
            class="waiting-room-issue-type-toggle"
            color="primary"
            divided
            mandatory
            variant="outlined"
          >
            <VBtn
              v-for="option in issueTypeFilterOptions"
              :key="option.value"
              :value="option.value"
              class="px-4 py-2 border"
            >
            <span class="text-wrap">
              {{ option.title }}
            </span>
            </VBtn>
          </VBtnToggle>
        </div>
        <div class="waiting-room-project-type-toggle-block mt-3">
          <VBtnToggle
            v-model="selectedProjectTypeFilter"
            class="waiting-room-project-type-toggle"
            color="primary"
            divided
            mandatory
            variant="outlined"
          >
            <VBtn
              v-for="option in projectTypeFilterOptions"
              :key="option.value"
              :value="option.value"
              class="px-4 py-2 border"
            >
            <span class="text-wrap">
              {{ option.title }}
            </span>
            </VBtn>
          </VBtnToggle>
        </div>
        <VBtn
          color="primary"
          class="ml-3 mt-3"
          :variant="selectedFileCount === 0 || bulkDownloadLoading ? 'outlined' : 'flat'"
          :loading="bulkDownloadLoading"
          :disabled="selectedFileCount === 0 || bulkDownloadLoading"
          prepend-icon="tabler-download"
          @click="downloadSelectedRows"
        >
          Download{{ selectedFileCount ? ` (${selectedFileCount})` : '' }}
        </VBtn>
      </VCol>
    </VRow>

    <VRow>
      <VCol
        cols="12"
      >

      </VCol>
    </VRow>

    <DivTable
      :key="waitingRoomTableRenderId"
      :model-value="selectedRowIds"
      :items="rows"
      :items-length="totalRows"
      :headers="waitingRoomHeaders"
      :loading="rowsLoading"
      :page="currentPage"
      :items-per-page="perPage"
      :items-per-page-options="perPageOptions"
      :sort-by="sortBy"
      :grid-id="waitingRoomTableRenderId"
      :grid-storage-id="waitingRoomTableStorageId"
      :no-data-text="showRejected ? 'No rejected files.' : 'No waiting room files.'"
      class="waiting-room-table"
      table-class="waiting-room-div-table"
      item-value="id"
      show-select
      hover
      @update:model-value="updateSelectedRows"
      @update:options="onWaitingRoomTableOptionsUpdate"
    >
      <template #item.filename="{ item }">
        <ProjectFilesTruncatedTextCell
          :text="item.raw.filename"
          :icon="getFileIcon(item.raw.filename)"
          :icon-color="getFileIconColor(item.raw.filename)"
          :icon-size="22"
          icon-class="mr-2"
        />
      </template>

      <template #item.folder="{ item }">
        <ProjectFilesTruncatedTextCell
          :text="item.raw.item_type_name"
          :tooltip-max-width="240"
        />
      </template>

      <template #item.issue_type="{ item }">
        <div class="d-flex align-center ga-1 flex-wrap">
          <span>{{ item.raw.issue_type === WAITING_ROOM_ONLY_DUPLICATE_ISSUE_TYPE ? 'Duplicate Files' : item.raw.issue_type_name || '-' }}</span>
          <VChip
            v-if="item.raw.is_partially_matched"
            size="x-small"
            density="compact"
            color="warning"
            variant="tonal"
            class="waiting-room-x-small-chip"
          >
            Partially Matched
          </VChip>
          <VChip
            v-if="item.raw.issue_type === WAITING_ROOM_ONLY_DUPLICATE_ISSUE_TYPE"
            size="x-small"
            density="compact"
            color="info"
            variant="tonal"
            class="waiting-room-x-small-chip"
          >
            Waiting Room Duplicate
          </VChip>
        </div>
      </template>

      <template #item.campaign_type="{ item }">
        <span>{{ getWaitingRoomProjectTypeTitle(item.raw.campaign_type) }}</span>
      </template>

      <template #item.campaign_name="{ item }">
        <div class="waiting-room-project-cell">
          <ProjectFilesItemTooltip
            v-if="!isStatusMismatchRow(item.raw) && !isVirtualRow(item.raw)"
            content="Change Project"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                icon="tabler-pencil"
                color="primary"
                variant="text"
                size="small"
                aria-label="Change Project"
                @click="openProjectDialog(item.raw)"
              />
            </span>
          </ProjectFilesItemTooltip>
          <ProjectFilesTruncatedTextCell
            class="waiting-room-project-cell__text"
            :text="item.raw.campaign_name || '-'"
            :tooltip-max-width="360"
          />
        </div>
      </template>

      <template #item.created="{ item }">
        <span>{{ formatFixedDateTime(item.raw.created) }}</span>
      </template>

      <template #item.created_by="{ item }">
        <ProjectFilesTruncatedTextCell
          :text="item.raw.created_by_display"
          :tooltip-max-width="320"
        />
      </template>

      <template #item.new_filename="{ item }">
        <div class="waiting-room-new-filename-cell">
          <ProjectFilesItemTooltip
            v-if="!isVirtualRow(item.raw)"
            content="Rename New File Name"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                icon="tabler-pencil"
                color="primary"
                variant="text"
                size="small"
                aria-label="Rename New File Name"
                @click="openRenameDialog(item.raw)"
              />
            </span>
          </ProjectFilesItemTooltip>
          <ProjectFilesTruncatedTextCell
            class="waiting-room-new-filename-cell__text"
            :text="item.raw.new_filename || item.raw.name"
            :tooltip-max-width="420"
          />
        </div>
      </template>

      <template #item.current_statuses="{ item }">
        <ProjectFilesTruncatedTextCell
          :text="formatStatuses(item.raw.current_statuses)"
          :tooltip-max-width="420"
        />
      </template>

      <template #item.expected_statuses="{ item }">
        <ProjectFilesTruncatedTextCell
          :text="formatStatuses(item.raw.expected_statuses)"
          :tooltip-max-width="420"
        />
      </template>

      <template #item.brand_book_codes="{ item }">
        <div class="waiting-room-brand-book-code-cell">
          <template v-if="isVirtualRow(item.raw) && getBrandBookCodeStates(item.raw).length">
            <span class="waiting-room-brand-book-code-cell__summary">
              <span class="waiting-room-brand-book-code-cell__text">
                {{ getBrandBookCodeStates(item.raw)[0].code }}
              </span>
              <VChip
                class="waiting-room-x-small-chip"
                size="x-small"
                density="compact"
                :color="getBrandBookCodeStates(item.raw)[0].passed ? 'success' : 'warning'"
                variant="tonal"
              >
                {{ getBrandBookCodeStates(item.raw)[0].passed ? 'Passed' : 'Not Passed' }}
              </VChip>
              <VChip
                v-if="getBrandBookCodeStates(item.raw).length > 1"
                class="waiting-room-brand-book-code-cell__count"
                size="x-small"
                density="compact"
                color="primary"
                variant="tonal"
              >
                +{{ getBrandBookCodeStates(item.raw).length - 1 }}
              </VChip>
            </span>
            <VMenu
              v-if="getBrandBookCodeStates(item.raw).length > 1"
              activator="parent"
              location="top"
              open-on-hover
              open-delay="100"
              close-delay="50"
              offset="5"
            >
              <VCard class="waiting-room-brand-book-code-menu pa-2">
                <ol class="waiting-room-brand-book-code-menu__list">
                  <li
                    v-for="(state, index) in getBrandBookCodeStates(item.raw)"
                    :key="`${state.code}-${index}`"
                    class="waiting-room-brand-book-code-menu__item d-flex align-center ga-2"
                  >
                    <span>{{ state.code }}</span>
                    <VChip
                      class="waiting-room-x-small-chip"
                      size="x-small"
                      density="compact"
                      :color="state.passed ? 'success' : 'warning'"
                      variant="tonal"
                    >
                      {{ state.passed ? 'Passed' : 'Not Passed' }}
                    </VChip>
                  </li>
                </ol>
              </VCard>
            </VMenu>
          </template>

          <ProjectFilesTruncatedTextCell
            v-else-if="getBrandBookCodes(item.raw).length === 1"
            :text="getBrandBookCodes(item.raw)[0]"
            empty-text=""
            :tooltip-max-width="320"
          />

          <template v-else-if="getBrandBookCodes(item.raw).length > 1">
            <span class="waiting-room-brand-book-code-cell__summary">
              <span class="waiting-room-brand-book-code-cell__text">
                {{ getBrandBookCodes(item.raw)[0] }}
              </span>
              <VChip
                class="waiting-room-brand-book-code-cell__count"
                size="x-small"
                density="compact"
                color="primary"
                variant="tonal"
              >
                +{{ getBrandBookCodes(item.raw).length - 1 }}
              </VChip>
            </span>
            <VMenu
              activator="parent"
              location="top"
              open-on-hover
              open-delay="100"
              close-delay="50"
              offset="5"
            >
              <VCard class="waiting-room-brand-book-code-menu pa-2">
                <ol class="waiting-room-brand-book-code-menu__list">
                  <li
                    v-for="(code, index) in getBrandBookCodes(item.raw)"
                    :key="`${code}-${index}`"
                    class="waiting-room-brand-book-code-menu__item"
                  >
                    {{ code }}
                  </li>
                </ol>
              </VCard>
            </VMenu>
          </template>
        </div>
      </template>

      <template #item.actions="{ item }">
        <div
          v-if="!showRejected"
          class="waiting-room-actions"
        >
          <ProjectFilesItemTooltip
            v-if="isPreviewableFile(item.raw.filename)"
            content="Preview"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                icon="tabler-eye"
                color="primary"
                variant="text"
                size=""
                class="pa-2"
                :loading="loadingPreview.has(item.raw.id)"
                @click="previewRow(item.raw)"
              />
            </span>
          </ProjectFilesItemTooltip>

          <ProjectFilesItemTooltip
            content="Download"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                icon="tabler-download"
                color="primary"
                variant="text"
                size=""
                class="pa-2"
                :loading="loadingDownload.has(item.raw.id)"
                @click="downloadRow(item.raw)"
              />
            </span>
          </ProjectFilesItemTooltip>

          <span class="waiting-room-chat-action">
            <ProjectFilesItemTooltip
              :content="getChatMessagesCountTooltip(item.raw)"
              :offset="6"
              v-slot="{ triggerProps }"
            >
              <span
                v-bind="triggerProps"
                class="waiting-room-chat-count-trigger"
              >
                <VChip
                  class="waiting-room-chat-count-chip"
                  color="primary"
                  variant="tonal"
                  size="x-small"
                  density="compact"
                >
                  {{ getChatMessagesCount(item.raw) }}
                </VChip>
              </span>
            </ProjectFilesItemTooltip>

            <ProjectFilesItemTooltip
              content="Chat"
              v-slot="{ triggerProps }"
            >
              <span
                v-bind="triggerProps"
                class="waiting-room-action-trigger"
              >
                <VBtn
                  icon="tabler-message-circle"
                  color="primary"
                  variant="text"
                  size=""
                  class="waiting-room-chat-btn pa-2"
                  @click="openChat(item.raw)"
                />
              </span>
            </ProjectFilesItemTooltip>
          </span>

          <VMenu
            v-if="isVirtualRow(item.raw)"
            location="top"
            open-on-hover
            open-delay="100"
            close-delay="50"
            offset="6"
          >
            <template #activator="{ props: menuProps }">
              <VBtn
                v-bind="menuProps"
                icon="tabler-info-circle"
                color="primary"
                variant="text"
                size=""
                class="pa-2"
                aria-label="Partially matched file information"
              />
            </template>
            <VCard class="pa-3" max-width="340">
              This file is already in File Management. Remaining linked items will move forward automatically when they reach the required status.
            </VCard>
          </VMenu>

          <ProjectFilesItemTooltip
            v-if="!isStatusMismatchRow(item.raw)"
            :content="isDuplicateFilesRow(item.raw)
              ? item.raw.issue_type === WAITING_ROOM_ONLY_DUPLICATE_ISSUE_TYPE
                ? 'Compare this file with the matching Status Mismatch file and choose which Waiting Room file to keep.'
                : 'View and compare Waiting Room Files with the existing Current File from items. In the result, choose the needed file and approve.'
              : 'Approve matches New File Name in this Project, validates item status, and uploads the file into Item Files. Required: New File Name.'"
            :max-width="300"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                color="success"
                variant="tonal"
                size="small"
                :prepend-icon="isDuplicateFilesRow(item.raw) ? 'tabler-arrows-diff' : 'tabler-check'"
                :loading="item.raw.actionLoading === 'accept'"
                :disabled="!(item.raw.new_filename || item.raw.name || '').trim()"
                @click="acceptRow(item.raw)"
              >
                {{ isDuplicateFilesRow(item.raw) ? 'View' : 'Approve' }}
              </VBtn>
            </span>
          </ProjectFilesItemTooltip>

          <ProjectFilesItemTooltip
            v-if="!isVirtualRow(item.raw) && !isStatusMismatchRow(item.raw)"
            content="Reject moves this file to Rejected."
            :max-width="220"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                color="error"
                variant="tonal"
                size="small"
                prepend-icon="tabler-x"
                :loading="item.raw.actionLoading === 'reject'"
                @click="rejectRow(item.raw)"
              >
                Reject
              </VBtn>
            </span>
          </ProjectFilesItemTooltip>
        </div>
        <div
          v-else
          class="waiting-room-actions"
        >
          <ProjectFilesItemTooltip
            v-if="isPreviewableFile(item.raw.filename)"
            content="Preview"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                icon="tabler-eye"
                color="primary"
                variant="text"
                size=""
                class="pa-2"
                :loading="loadingPreview.has(item.raw.id)"
                @click="previewRow(item.raw)"
              />
            </span>
          </ProjectFilesItemTooltip>

          <ProjectFilesItemTooltip
            content="Download"
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                icon="tabler-download"
                color="primary"
                variant="text"
                size=""
                class="pa-2"
                :loading="loadingDownload.has(item.raw.id)"
                @click="downloadRow(item.raw)"
              />
            </span>
          </ProjectFilesItemTooltip>

          <span class="waiting-room-chat-action">
            <ProjectFilesItemTooltip
              :content="getChatMessagesCountTooltip(item.raw)"
              :offset="6"
              v-slot="{ triggerProps }"
            >
              <span
                v-bind="triggerProps"
                class="waiting-room-chat-count-trigger"
              >
                <VChip
                  class="waiting-room-chat-count-chip"
                  color="primary"
                  variant="tonal"
                  size="x-small"
                  density="compact"
                >
                  {{ getChatMessagesCount(item.raw) }}
                </VChip>
              </span>
            </ProjectFilesItemTooltip>

            <ProjectFilesItemTooltip
              content="Chat"
              v-slot="{ triggerProps }"
            >
              <span
                v-bind="triggerProps"
                class="waiting-room-action-trigger"
              >
                <VBtn
                  icon="tabler-message-circle"
                  color="primary"
                  variant="text"
                  size=""
                  class="waiting-room-chat-btn pa-2"
                  @click="openChat(item.raw)"
                />
              </span>
            </ProjectFilesItemTooltip>
          </span>

          <ProjectFilesItemTooltip
            content="Restore returns this file to Waiting Room for future Approve or Reject."
            v-slot="{ triggerProps }"
          >
            <span
              v-bind="triggerProps"
              class="waiting-room-action-trigger"
            >
              <VBtn
                color="success"
                variant="tonal"
                size="small"
                prepend-icon="tabler-refresh"
                :loading="item.raw.actionLoading === 'restore'"
                @click="restoreRow(item.raw)"
              >
                Restore
              </VBtn>
            </span>
          </ProjectFilesItemTooltip>
        </div>
      </template>
      <template #bottom>
        <div class="waiting-room-table-footer mt-3">
          <div class="waiting-room-table-footer__left">
            <ProjectFilesItemTooltip
              v-if="!props.syncQuery"
              content="Open Waiting Room page"
              v-slot="{ triggerProps }"
            >
              <span
                v-bind="triggerProps"
                class="waiting-room-action-trigger"
                @click.capture="onWaitingRoomPageLinkClick"
              >
                <VBtn
                  icon="tabler-external-link"
                  color="primary"
                  variant="text"
                  size="small"
                  aria-label="Open Waiting Room page"
                  :to="waitingRoomPageLocation"
                />
              </span>
            </ProjectFilesItemTooltip>
          </div>

          <div class="waiting-room-table-footer__pagination">
            <span>Rows per page</span>
            <VSelect
              :model-value="perPage"
              :items="perPageOptions"
              density="compact"
              hide-details
              style="max-inline-size: 100px"
              @update:modelValue="changeItemsPerPage"
            />
            <span>{{ visibleRangeText }}</span>
            <div style="display: contents;">
              <VBtn
                class="page-button"
                icon="material-symbols:first-page"
                variant="text"
                :disabled="rowsLoading || currentPage <= 1"
                @click="changePage(1)"
              />
              <VBtn
                class="page-button"
                icon="tabler-chevron-left"
                variant="text"
                :disabled="rowsLoading || currentPage <= 1"
                @click="changePage(currentPage - 1)"
              />
            </div>
            <span>{{ currentPage }}</span>
            <div style="display: contents;">
              <VBtn
                class="page-button"
                icon="tabler-chevron-right"
                variant="text"
                :disabled="rowsLoading || currentPage >= totalPages"
                @click="changePage(currentPage + 1)"
              />
              <VBtn
                class="page-button"
                icon="material-symbols:last-page"
                variant="text"
                :disabled="rowsLoading || currentPage >= totalPages"
                @click="changePage(totalPages)"
              />
            </div>
          </div>
        </div>
      </template>
    </DivTable>

    <GlobalChatDialog
      v-model="showChatDialog"
      :item-id="selectedChatItemId"
      :item-type="selectedChatItemType"
      @on-message-added="handleChatMessageAdded"
      @on-close="handleChatClose"
    />

    <ProjectFilePreviewDialog
      v-model="previewDialogVisible"
      :url="previewUrl"
      :filename="previewFilename"
      :loading="isPreviewLoading"
    />

    <ProjectFilesWaitingRoomRenameDialog
      v-model="showRenameDialog"
      :file="selectedRenameFile"
      :loading="renameLoading"
      @save="saveNewFilename"
    />

    <ProjectFilesSelectProjectDialog
      v-model="showProjectDialog"
      v-model:project="selectedProjectForChange"
      :projects="projectChangeOptions"
      :loading="projectLoading"
      :options-loading="projectChangeOptionsLoading"
      :has-more-options="hasMoreProjectChangeOptions"
      searchable
      confirm-text="Save"
      @confirm="saveProject"
      @search="onProjectChangeSearch"
      @load-more="loadMoreProjectChangeOptions"
    />

    <ProjectFilesWaitingRoomDuplicateComparisonDialog
      v-model="showDuplicateComparisonDialog"
      :file="pendingDuplicateApproveRow"
      :loading="pendingDuplicateApproveRow?.actionLoading === 'accept'"
      @cancel="clearDuplicateComparison"
      @confirm="confirmDuplicateComparison"
      @stale="refresh"
    />

    <ProjectFilesWaitingRoomStatusMismatchDialog
      v-model="showStatusMismatchDialog"
      :title="statusMismatchDialogTitle"
      :mismatches="statusMismatchDialogItems"
    />
  </div>
</template>

<style scoped lang="scss">
.project-files-waiting-room-block {
  container: waiting-room / inline-size;
  block-size: 100%;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  min-inline-size: 0;
  overflow: hidden;

  > .v-row {
    flex: 0 0 auto;
  }
}

.waiting-room-filter-row,
.waiting-room-filter-column {
  min-inline-size: 0;
}

.waiting-room-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  min-inline-size: 0;
  overflow: hidden;
  padding: 10px 0 !important;

  :deep(.lcx-div-table) {
    block-size: 100%;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-block-size: 0;
    min-inline-size: 0;
    overflow: hidden;
    padding-block: 0 !important;
  }

  :deep(.div-table-wrapper) {
    flex: 1 1 auto;
    max-block-size: none;
    max-height: none;
    min-height: 280px;
    min-block-size: 0;
    min-inline-size: 0;
    overscroll-behavior: contain;
  }

  :deep(.div-table-bottom-scrollbar) {
    flex: 0 0 14px;
  }
}

.waiting-room-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.waiting-room-chat-action {
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.waiting-room-chat-count-trigger {
  display: inline-flex;
  margin: 0;
}

.waiting-room-chat-count-chip {
  flex: 0 0 auto;
  min-inline-size: 18px;
  justify-content: center;
  margin: 0;
  height: auto;
}

.waiting-room-chat-btn {
  min-inline-size: 28px;
  margin-inline-start: 0;
}

.waiting-room-new-filename-cell,
.waiting-room-brand-book-code-cell,
.waiting-room-project-cell {
  display: flex;
  align-items: center;
  inline-size: 100%;
  min-inline-size: 0;
  gap: 4px;
}

.waiting-room-new-filename-cell__text,
.waiting-room-project-cell__text {
  flex: 1 1 auto;
  min-inline-size: 0;
}

.waiting-room-brand-book-code-cell {
  position: relative;
}

.waiting-room-brand-book-code-cell__summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-inline-size: 100%;
  min-inline-size: 0;
}

.waiting-room-brand-book-code-cell__text {
  display: block;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waiting-room-brand-book-code-cell__count {
  flex: 0 0 auto;
  height: unset;
}

.waiting-room-brand-book-code-menu {
  max-block-size: 260px;
  max-inline-size: 320px;
  overflow-y: auto;
}

.waiting-room-brand-book-code-menu__list {
  margin: 0;
  padding-inline-start: 20px;
}

.waiting-room-brand-book-code-menu__item {
  padding: 3px 6px;
  font-size: 0.8125rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.waiting-room-action-trigger {
  display: inline-flex;
}

.waiting-room-issue-type-toggle-block,
.waiting-room-project-type-toggle-block {
  display: grid;
  max-inline-size: 100%;
  min-inline-size: 0;

  :deep(.v-btn-toggle) {
    flex-wrap: wrap;
  }
}

@container waiting-room (inline-size < 1100px) {
  .waiting-room-filter-column {
    flex: 0 0 50%;
    max-inline-size: 50%;
  }
}

@container waiting-room (inline-size < 680px) {
  .waiting-room-filter-column {
    flex-basis: 100%;
    max-inline-size: 100%;
  }
}

.waiting-room-table-footer {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.waiting-room-table-footer__left,
.waiting-room-table-footer__pagination {
  display: flex;
  align-items: center;
  gap: 12px;
}

.waiting-room-table-footer__left {
  min-inline-size: 40px;
}

.waiting-room-table-footer__pagination {
  justify-content: flex-end;
  flex-wrap: wrap;

  .page-button {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    padding: 0;
  }

  .page-button.v-btn--disabled {
    opacity: 0.3 !important;
  }
}

@container (inline-size < 500px) {
  .waiting-room-table-footer {
    gap: 4px;
    font-size: 12px;
  }

  .waiting-room-table-footer__pagination {
    gap: 4px;

    .page-button {
      min-width: 16px !important;
      inline-size: 16px !important;
      padding: 0;
    }

    .v-field {
      font-size: 12px;
      padding-right: 8px;
      max-width: 76px;

      .v-field__input {
        padding-left: 8px;
      }
    }
  }
}

.waiting-room-x-small-chip {
  flex: 0 0 auto;
  height: unset;
}
</style>
